import React from 'react';
import { Table, Tabs, Tab } from 'react-bootstrap';

const LineItemsComparison = ({ model1Name, model2Name, model3Name, lineItems }) => {
  // Debug: Log the received lineItems structure
  console.log('LineItemsComparison received lineItems:', lineItems);
  console.log('lineItems.model1 type:', typeof lineItems?.model1, 'isArray:', Array.isArray(lineItems?.model1));
  
  // Helper function to parse line items if they're JSON strings.
  // Preserves decimal precision by converting JSON numeric literals to strings
  // before parsing (e.g., 65.00 stays as "65.00" instead of becoming JS number 65).
  const parseLineItems = (items) => {
    if (!items) return [];
    if (Array.isArray(items)) return items;
    if (typeof items === 'string') {
      try {
        // Convert JSON number values to strings to preserve trailing zeroes.
        // This regex matches numeric values that appear after ":" (JSON object values)
        // but not numbers inside quoted strings. It works by matching the key-value
        // pattern specifically: "key": number
        const preserved = items.replace(
          /"([^"]+)"\s*:\s*(-?\d+(?:\.\d+)?)\s*(?=[,}\]])/g,
          '"$1":"$2"'
        );
        const parsed = JSON.parse(preserved);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error('Failed to parse line items:', e);
        return [];
      }
    }
    // Handle JsonElement or other object types
    if (typeof items === 'object') {
      // If it's already an object with array-like properties, try to convert it
      if (items.length !== undefined) {
        return Array.from(items);
      }
    }
    return [];
  };

  // Parse all line item arrays
  const model1Items = parseLineItems(lineItems.model1);
  const model2Items = parseLineItems(lineItems.model2);
  const model3Items = parseLineItems(lineItems.model3);
  const finalItems = parseLineItems(lineItems.final);

  // Extract all unique keys from all line items across all models
  const getAllKeys = () => {
    const keySets = [];
    
    // Get keys from model1 line items
    model1Items.forEach(item => {
      if (item && typeof item === 'object') {
        keySets.push(Object.keys(item));
      }
    });
    
    // Get keys from model2 line items
    model2Items.forEach(item => {
      if (item && typeof item === 'object') {
        keySets.push(Object.keys(item));
      }
    });
    
    // Get keys from model3 line items if available
    model3Items.forEach(item => {
      if (item && typeof item === 'object') {
        keySets.push(Object.keys(item));
      }
    });
    
    // Get keys from final line items if available
    finalItems.forEach(item => {
      if (item && typeof item === 'object') {
        keySets.push(Object.keys(item));
      }
    });
    
    // Flatten and get unique keys
    return [...new Set(keySets.flat())];
  };
  
  const maxLineItems = Math.max(
    model1Items.length,
    model2Items.length,
    model3Items.length,
    finalItems.length
  );
  
  // If no line items exist, show a message
  if (maxLineItems === 0) {
    return (
      <div className="mt-4">
        <h6>Line Items Comparison</h6>
        <p className="text-muted">No line items found in extraction results.</p>
      </div>
    );
  }
  
  // Create an array of indices for the line items
  const lineIndices = Array.from({ length: maxLineItems }, (_, i) => i);
  
  // Get unique keys (fields) to display in each line item
  const uniqueKeys = getAllKeys();

  return (
    <div className="mt-4">
      <h6>Line Items Comparison</h6>
      
      <div className="mb-4">
        <Tabs defaultActiveKey={0} id="line-items-tabs">
          {lineIndices.map(index => (
            <Tab key={index} eventKey={index} title={`Line Item ${index + 1}`}>
              <div className="table-scroll-wrapper">
                <Table striped bordered hover className="mt-3">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>{model1Name}</th>
                      <th>{model2Name}</th>
                      {model3Name && <th>{model3Name}</th>}
                      <th>Final Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uniqueKeys.map(key => (
                      <tr key={key}>
                        <td>{key}</td>
                        <td style={{backgroundColor: (finalItems[index]?.[key] != null && 
                                 model1Items[index]?.[key] != null && 
                                 String(model1Items[index][key]) !== String(finalItems[index][key])) ||
                                 (finalItems[index]?.[key] != null && 
                                 (model1Items[index]?.[key] == null || model1Items[index]?.[key] === '')) ? 
                                 '#ffd6d6' : 'inherit'}}>
                          {model1Items[index] && model1Items[index][key] != null ? 
                          String(model1Items[index][key]) : 'N/A'}
                      </td>
                      <td style={{backgroundColor: (finalItems[index]?.[key] != null && 
                                 model2Items[index]?.[key] != null &&
                                 String(model2Items[index][key]) !== String(finalItems[index][key])) ||
                                 (finalItems[index]?.[key] != null && 
                                 (model2Items[index]?.[key] == null || model2Items[index]?.[key] === '')) ? 
                                 '#ffd6d6' : 'inherit'}}>
                          {model2Items[index] && model2Items[index][key] != null ? 
                          String(model2Items[index][key]) : 'N/A'}
                      </td>
                      {model3Name && (
                          <td style={{backgroundColor: (finalItems[index]?.[key] != null && 
                                     model3Items[index]?.[key] != null &&
                                     String(model3Items[index][key]) !== String(finalItems[index][key])) ||
                                     (finalItems[index]?.[key] != null && 
                                     (model3Items[index]?.[key] == null || model3Items[index]?.[key] === '')) ? 
                                     '#ffd6d6' : 'inherit'}}>
                            {model3Items[index] && model3Items[index][key] != null ? 
                            String(model3Items[index][key]) : 'N/A'}
                          </td>
                      )}
                      <td style={{backgroundColor: (!finalItems[index] || 
                                 finalItems[index][key] == null) ? '#ffd6d6' : 'inherit'}}>
                          {finalItems[index] && finalItems[index][key] != null ? 
                          String(finalItems[index][key]) : 'N/A'}
                      </td>
                    </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Tab>
          ))}
        </Tabs>
      </div>
    </div>
  );
};

export default LineItemsComparison;